import { useStoryblokBridge } from '@storyblok/js';
import type { ISbStoriesParams, ISbStoryData } from 'storyblok-js-client';
import { postTokensReplaceV1 } from '@tapp/api/fetch';

const TOKEN_PATTERN = /%[A-Z][A-Z0-9_]{3,}%/;

interface UseLiveStoryOptions {
  /** The relations the page fetch resolved, so an edit pushed by the editor resolves them the same way. */
  resolveRelations?: ISbStoriesParams['resolve_relations'];
  /** The meta data the page fetch used to replace back-end tokens (`%TITLE%`), applied to every edit as well. */
  metaData?: Record<string, unknown>;
}

/**
 * Keeps a fetched story in sync with the Storyblok visual editor.
 *
 * Outside the editor the returned ref simply holds the story as fetched. Inside the editor the Storyblok bridge
 * pushes every edit into the ref, after replacing back-end tokens the way the page fetch did.
 */
export default function useLiveStory<T>(
  story: ISbStoryData<T> | undefined,
  { resolveRelations, metaData }: UseLiveStoryOptions = {},
) {
  const liveStory = shallowRef<ISbStoryData<T> | undefined>(story);

  if (!story || !isInStoryblokVisualEditor()) {
    return liveStory;
  }

  useStoryblokBridge(
    story.id,
    async function onUpdatedStoryDataReceived(updatedStoryData) {
      const updatedStoryDataString = JSON.stringify(updatedStoryData);

      if (!TOKEN_PATTERN.test(updatedStoryDataString)) {
        liveStory.value = updatedStoryData;
        return;
      }

      const replacedStoryData = await postTokensReplaceV1({
        input: updatedStoryDataString,
        ...metaData,
      });

      liveStory.value = JSON.parse(replacedStoryData.output) as ISbStoryData<T>;
    },
    { resolveRelations },
  );

  return liveStory;
}
